import {AxiosInstance} from "axios";
import {BookingLinkCombine, BookingLinkLaboratory, BookingLinkMode} from "@/agenda/lib/bookingLink";

/**
 * A named booking-link template, shared by the whole pharmacy.
 */
export interface BookingLinkPreset {
    id: number;
    /** Opaque public identifier; the only thing the patient URL carries. */
    code: string;
    /** Null until the patient-web host is configured on the API. */
    url: string | null;
    /** Null for a code minted on a copy and never saved as a template. */
    name: string | null;
    mode: BookingLinkMode;
    /** Access point ids of the selected collaborators. */
    operator_ids: number[];
    combine: BookingLinkCombine | null;
    appointment_type_id: number | null;
    laboratory: BookingLinkLaboratory | null;
    min_notice_minutes: number;
    horizon_days: number;
    archived_at: string | null;
}

/**
 * The saved links of a pharmacy, and whether links are shareable at all.
 *
 * `bookingEnabled` is false while the server does not know the address of the
 * public booking page: no code can then be turned into a link, so the whole
 * affordance is withheld rather than offering the pharmacist a draft they
 * could not hand to a patient. It answers per environment, which is why it
 * comes from the server instead of the build.
 */
export interface BookingLinkPresetIndex {
    presets: BookingLinkPreset[];
    bookingEnabled: boolean;
}

export interface BookingLinkPresetRequest {
    /** Null mints a code without saving it as a template. */
    name: string | null;
    mode: BookingLinkMode;
    operator_ids: number[];
    combine: BookingLinkCombine | null;
    appointment_type_id: number | null;
    laboratory_id: number | null;
    min_notice_minutes: number;
    horizon_days: number;
}

export interface BookingLinkSmsRequest {
    phone: string;
    /**
     * Set only when the number still matches the file of the patient picked
     * in the search: it is what lets the server mint a link authenticating
     * them, so it must never travel with someone else's number.
     */
    customer_id?: number;
    /** The booking code to send; the server reads its rules and composes the URL. */
    code: string;
}

/**
 * Booking-link presets and SMS sending of the « Lien de RDV » modal.
 *
 * Two clients, like LaboratoryResource: a preset is pharmacy configuration and
 * lives on the API, while the sending lives on the HDS, where the patient, the
 * SMS credits and the RCS channel are.
 */
const BookingLinkPresetResource = (apiClient: AxiosInstance, hdsClient: AxiosInstance) => {
    return {
        /** Get the pharmacy's saved booking links. */
        async index(organizationId: number): Promise<BookingLinkPresetIndex> {
            const response = await apiClient.get(`organizations/${organizationId}/appointment-link-presets`);

            return {
                presets: response.data.data,
                // Absent from an older API: read as « not configured », which
                // is the state the flag describes on every environment that
                // predates the public page.
                bookingEnabled: response.data.meta?.booking_enabled === true
            };
        },

        /** Save the drafted link as a named template. */
        async create(organizationId: number, request: BookingLinkPresetRequest): Promise<BookingLinkPreset> {
            const response = await apiClient.post(`organizations/${organizationId}/appointment-link-presets`, {data: request});

            return response.data.data;
        },

        /** Update a template. */
        async update(presetId: number, request: BookingLinkPresetRequest): Promise<BookingLinkPreset> {
            const response = await apiClient.patch(`appointment-link-presets/${presetId}`, {data: request});

            return response.data.data;
        },

        /** Delete a template. */
        async delete(presetId: number): Promise<void> {
            await apiClient.delete(`appointment-link-presets/${presetId}`);
        },

        /**
         * Send the link to a patient by RCS, with an SMS fallback.
         *
         * The server composes the URL and the wording from these ids — it owns
         * the patient-web host — and decides between an anonymous link and one
         * authenticating `customer_id`. Resolves to whether the patient will
         * land already identified.
         */
        async sendSms(organizationId: number, request: BookingLinkSmsRequest): Promise<{authenticated: boolean}> {
            const response = await hdsClient.post(`organizations/${organizationId}/appointment-links/sms`, {data: request});

            // RawResponseResource, wrapped under `data` like every JsonResource.
            return {authenticated: Boolean(response.data?.data?.authenticated)};
        }
    };
};

export default BookingLinkPresetResource;
